import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { api } from '@/utils/api';
import { Plus, ClipboardCheck, Brain, Loader2, AlertTriangle } from 'lucide-react';
import Layout from '@/components/Layout';

const conditionColors = { 1: 'bg-red-600', 2: 'bg-red-500', 3: 'bg-red-400', 4: 'bg-orange-500', 5: 'bg-orange-400', 6: 'bg-yellow-500', 7: 'bg-yellow-400', 8: 'bg-green-400', 9: 'bg-green-500', 10: 'bg-green-600' };

export default function Inspections() {
  const { i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const [inspections, setInspections] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [analyzing, setAnalyzing] = useState(null);
  const [formData, setFormData] = useState({ property_id: '', unit_id: '', inspector_name: '', inspection_type: 'routine', condition_score: '7', notes: '', defects: '' });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [inspRes, propsRes] = await Promise.all([api.getInspections(), api.getProperties()]);
      setInspections(inspRes.data);
      setProperties(propsRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.createInspection({
        ...formData,
        condition_score: parseInt(formData.condition_score),
        defects: formData.defects ? formData.defects.split(',').map(d => d.trim()) : []
      });
      setDialogOpen(false);
      setFormData({ property_id: '', unit_id: '', inspector_name: '', inspection_type: 'routine', condition_score: '7', notes: '', defects: '' });
      fetchData();
    } catch (e) { console.error(e); }
  };

  const handleAIAnalyze = async (id) => {
    setAnalyzing(id);
    try { await api.aiAnalyzeInspection(id); fetchData(); }
    catch (e) { console.error(e); }
    finally { setAnalyzing(null); }
  };

  const getPropertyName = (id) => properties.find(p => p.id === id)?.name || '-';
  const typeLabels = isAr ? { routine: 'دوري', move_in: 'استلام', move_out: 'تسليم', safety: 'سلامة', structural: 'إنشائي' } : { routine: 'Routine', move_in: 'Move-in', move_out: 'Move-out', safety: 'Safety', structural: 'Structural' };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center"><ClipboardCheck className="h-5 w-5 text-indigo-600" strokeWidth={1.5} /></div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-gray-900">{isAr ? 'الفحوصات والتفتيش' : 'Inspections'}</h1>
              <p className="text-sm text-gray-500">{isAr ? 'فحص المباني والوحدات مع تحليل ذكاء اصطناعي' : 'Building inspections with AI defect analysis'}</p>
            </div>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild><Button className="gradient-primary text-white rounded-xl"><Plus className="h-4 w-4 me-2" /> {isAr ? 'فحص جديد' : 'New Inspection'}</Button></DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>{isAr ? 'إنشاء فحص جديد' : 'Create New Inspection'}</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>{isAr ? 'العقار' : 'Property'}*</Label>
                    <Select value={formData.property_id} onValueChange={v => setFormData({ ...formData, property_id: v })}>
                      <SelectTrigger><SelectValue placeholder={isAr ? 'اختر العقار' : 'Select property'} /></SelectTrigger>
                      <SelectContent>{properties.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>{isAr ? 'نوع الفحص' : 'Type'}*</Label>
                    <Select value={formData.inspection_type} onValueChange={v => setFormData({ ...formData, inspection_type: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{Object.entries(typeLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>{isAr ? 'المفتش' : 'Inspector'}*</Label><Input value={formData.inspector_name} onChange={e => setFormData({ ...formData, inspector_name: e.target.value })} required /></div>
                  <div><Label>{isAr ? 'الحالة (1-10)' : 'Condition (1-10)'}*</Label><Input type="number" min="1" max="10" value={formData.condition_score} onChange={e => setFormData({ ...formData, condition_score: e.target.value })} required /></div>
                </div>
                <div><Label>{isAr ? 'ملاحظات' : 'Notes'}*</Label><textarea className="w-full border rounded-xl p-3 text-sm min-h-[80px]" value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })} required /></div>
                <div><Label>{isAr ? 'العيوب (فاصلة)' : 'Defects (comma-separated)'}</Label><Input value={formData.defects} onChange={e => setFormData({ ...formData, defects: e.target.value })} placeholder={isAr ? 'تشقق، تسرب، عفن' : 'crack, leak, mold'} /></div>
                <Button type="submit" className="w-full gradient-primary text-white rounded-xl">{isAr ? 'حفظ الفحص' : 'Save Inspection'}</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-4 gap-4">
          <Card className="shadow-soft border-gray-100"><CardContent className="pt-5"><div className="text-2xl font-heading font-bold">{inspections.length}</div><div className="text-xs text-gray-500">{isAr ? 'إجمالي الفحوصات' : 'Total Inspections'}</div></CardContent></Card>
          <Card className="shadow-soft border-gray-100"><CardContent className="pt-5"><div className="text-2xl font-heading font-bold text-green-600">{inspections.filter(i => i.condition_score >= 7).length}</div><div className="text-xs text-gray-500">{isAr ? 'حالة جيدة' : 'Good Condition'}</div></CardContent></Card>
          <Card className="shadow-soft border-gray-100"><CardContent className="pt-5"><div className="text-2xl font-heading font-bold text-yellow-600">{inspections.filter(i => i.condition_score >= 4 && i.condition_score < 7).length}</div><div className="text-xs text-gray-500">{isAr ? 'يحتاج متابعة' : 'Needs Attention'}</div></CardContent></Card>
          <Card className="shadow-soft border-gray-100"><CardContent className="pt-5"><div className="text-2xl font-heading font-bold text-red-600">{inspections.filter(i => i.condition_score < 4).length}</div><div className="text-xs text-gray-500">{isAr ? 'حرج' : 'Critical'}</div></CardContent></Card>
        </div>

        <Card className="shadow-soft border-gray-100">
          <CardContent className="pt-6">
            {loading ? <div className="text-center py-8">Loading...</div> : inspections.length === 0 ? (
              <div className="text-center py-12 text-gray-400"><ClipboardCheck className="h-12 w-12 mx-auto mb-3 opacity-50" /><p>{isAr ? 'لا توجد فحوصات' : 'No inspections yet'}</p></div>
            ) : (
              <Table>
                <TableHeader><TableRow>
                  <TableHead>{isAr ? 'العقار' : 'Property'}</TableHead>
                  <TableHead>{isAr ? 'النوع' : 'Type'}</TableHead>
                  <TableHead>{isAr ? 'المفتش' : 'Inspector'}</TableHead>
                  <TableHead>{isAr ? 'الحالة' : 'Condition'}</TableHead>
                  <TableHead>{isAr ? 'العيوب' : 'Defects'}</TableHead>
                  <TableHead>{isAr ? 'تحليل AI' : 'AI Analysis'}</TableHead>
                  <TableHead>{isAr ? 'التاريخ' : 'Date'}</TableHead>
                </TableRow></TableHeader>
                <TableBody>{inspections.map(insp => (
                  <TableRow key={insp.id}>
                    <TableCell className="font-medium">{getPropertyName(insp.property_id)}</TableCell>
                    <TableCell>{typeLabels[insp.inspection_type] || insp.inspection_type}</TableCell>
                    <TableCell>{insp.inspector_name}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${conditionColors[insp.condition_score] || 'bg-gray-400'}`} />
                        <span className="font-mono text-sm">{insp.condition_score}/10</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {insp.defects?.length > 0 ? (
                        <div className="flex flex-wrap gap-1">{insp.defects.slice(0, 3).map((d, i) => <span key={i} className="text-[10px] px-1.5 py-0.5 bg-red-50 text-red-700 rounded">{d}</span>)}</div>
                      ) : <span className="text-xs text-green-600">{isAr ? 'لا يوجد' : 'None'}</span>}
                    </TableCell>
                    <TableCell>
                      {insp.ai_analysis ? (
                        <span className="text-xs text-green-600 font-medium">{isAr ? 'تم التحليل' : 'Analyzed'}</span>
                      ) : (
                        <Button variant="outline" size="sm" className="rounded-lg text-xs" onClick={() => handleAIAnalyze(insp.id)} disabled={analyzing === insp.id}>
                          {analyzing === insp.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Brain className="h-3 w-3 me-1" />}
                          {isAr ? 'تحليل' : 'Analyze'}
                        </Button>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">{new Date(insp.created_at).toLocaleDateString()}</TableCell>
                  </TableRow>
                ))}</TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
